/**
 * The people behind an application (G6): the recruiter who wrote, the
 * hiring manager from the screen, the friend who referred. Kept on the
 * application they belong to, next to the follow-up date and the notes.
 *
 * One card per person: a name, a role at an organisation, a way to reach
 * them, and what to remember. Add and edit inline; remove takes the card
 * away for good.
 */

import { useState } from "react";
import { api, ApiError } from "./auth";
import { ConfirmRemove } from "./fields";

export interface ContactView {
  contactId: string;
  fullName: string;
  email: string | null;
  org: string | null;
  role: string | null;
  linkedinUrl: string | null;
  notes: string | null;
  createdAt: string;
}

type Draft = { fullName: string; role: string; org: string; email: string; linkedinUrl: string; notes: string };

const EMPTY: Draft = { fullName: "", role: "", org: "", email: "", linkedinUrl: "", notes: "" };

function toDraft(c: ContactView): Draft {
  return { fullName: c.fullName, role: c.role ?? "", org: c.org ?? "", email: c.email ?? "", linkedinUrl: c.linkedinUrl ?? "", notes: c.notes ?? "" };
}

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.messages.join(" ");
  return err instanceof Error ? err.message : String(err);
}

/** "Recruiter at Huntress", "Huntress", or nothing. */
function where(c: ContactView): string {
  return [c.role, c.org].filter(Boolean).join(" at ");
}

export function People({ jobId, people, onChanged }: {
  jobId: string;
  people: ContactView[];
  onChanged: (people: ContactView[]) => void;
}) {
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = (c: ContactView | null) => {
    setEditing(c ? c.contactId : "new");
    setDraft(c ? toDraft(c) : EMPTY);
    setError(null);
  };
  const stop = () => { setEditing(null); setDraft(EMPTY); setError(null); };

  const submit = () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const body = JSON.stringify(draft);
    const call = editing === "new"
      ? api<{ contact: ContactView }>(`/api/applications/${jobId}/contacts`, { method: "POST", body })
      : api<{ contact: ContactView }>(`/api/contacts/${editing}`, { method: "PUT", body });
    call
      .then(({ contact }) => {
        onChanged(editing === "new" ? [...people, contact] : people.map((p) => (p.contactId === contact.contactId ? contact : p)));
        stop();
      })
      .catch((err) => setError(describe(err)))
      .finally(() => setBusy(false));
  };

  const remove = (c: ContactView) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    api<{ removed: boolean }>(`/api/contacts/${c.contactId}`, { method: "DELETE" })
      .then(() => { onChanged(people.filter((p) => p.contactId !== c.contactId)); if (editing === c.contactId) stop(); })
      .catch((err) => setError(describe(err)))
      .finally(() => setBusy(false));
  };

  const field = (key: keyof Draft, label: string, placeholder: string, type = "text") => (
    <label className="person-field">
      <span className="cap">{label}</span>
      <input
        className="input"
        type={type}
        value={draft[key]}
        placeholder={placeholder}
        disabled={busy}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
      />
    </label>
  );

  const form = (
    <div className="person-form">
      <div className="person-grid">
        {field("fullName", "Name", "Jane Doe")}
        {field("role", "Role", "Recruiter")}
        {field("org", "Organisation", "The employer, or their agency")}
        {field("email", "Email", "jane@example.com", "email")}
        {field("linkedinUrl", "LinkedIn", "linkedin.com/in/…", "url")}
      </div>
      <label className="person-field">
        <span className="cap">What to remember</span>
        <textarea
          className="input notes"
          rows={2}
          value={draft.notes}
          placeholder="What they said, what they asked for, when they said they'd decide."
          disabled={busy}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
        />
      </label>
      <div className="row tight-row">
        <button className="btn small" disabled={busy || !draft.fullName.trim()} onClick={submit}>
          {busy ? "Saving…" : editing === "new" ? "Add" : "Save"}
        </button>
        <button className="btn ghost small" disabled={busy} onClick={stop}>Cancel</button>
        {error && <span className="err small">{error}</span>}
      </div>
    </div>
  );

  return (
    <div className="people">
      <div className="people-head">
        <span className="field-label">People</span>
        {editing === null && (
          <button className="step-link" onClick={() => start(null)}>Add a person</button>
        )}
      </div>
      {people.length === 0 && editing === null && (
        <p className="muted small">The recruiter who wrote, the manager from the screen, the friend who referred — kept here, next to the notes.</p>
      )}
      <ul className="person-list">
        {people.map((c) => (
          <li key={c.contactId}>
            {editing === c.contactId ? form : (
              <>
                <div className="person-line">
                  <span className="person-name">{c.fullName}</span>
                  {where(c) && <span className="muted small"> — {where(c)}</span>}
                  <span className="person-links">
                    {c.email && <a className="small" href={`mailto:${c.email}`}>{c.email}</a>}
                    {c.linkedinUrl && <a className="small" href={c.linkedinUrl} target="_blank" rel="noreferrer noopener">LinkedIn</a>}
                    <button className="step-link small" disabled={busy} onClick={() => start(c)}>Edit</button>
                    <ConfirmRemove onRemove={() => void remove(c)} busy={busy} />
                  </span>
                </div>
                {c.notes && <p className="person-notes small">{c.notes}</p>}
              </>
            )}
          </li>
        ))}
        {editing === "new" && <li>{form}</li>}
      </ul>
      {error && editing === null && <p className="err small">{error}</p>}
    </div>
  );
}
